import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { useMyPendingPayments, useVerifyPendingPayment } from '../../api/hooks';
import { Spinner } from '../ui';
import { cn, fmtDateTime } from '../../lib/utils';

const PAGE_SIZE = 20;

const RECOVERY_STATE_STYLE = {
  pending:          'text-warn bg-warn/10 border-warn/30',
  support_required: 'text-bear bg-bear/10 border-bear/30',
};

const RECOVERY_STATE_LABEL = {
  pending:          'AWAITING CONFIRMATION',
  support_required: 'NEEDS REVIEW',
};

// Controlled copy for every backend outcome. Raw provider or backend error
// bodies are never shown, and no wording tells a customer to pay again.
const OUTCOME_COPY = {
  paid: {
    tone: 'bull',
    icon: CheckCircle2,
    text: 'Payment confirmed. Your purchase has been applied.',
  },
  pending: {
    tone: 'warn',
    icon: Clock,
    text: 'AlgoSys has not yet confirmed a captured payment for this order. If you completed the payment it may still be processing — you can check again in a little while.',
  },
  support_required: {
    tone: 'bear',
    icon: AlertTriangle,
    text: 'This payment needs a manual review by the AlgoSys team before it can be applied. Please do not make another payment for it.',
  },
};

// A failed status check proves nothing about the payment itself, so this must
// never be worded as a payment failure.
const CHECK_FAILED_TEXT =
  'We could not check this payment right now. The order has not been cancelled and no new payment was created. Please try again shortly.';

function fmtAmount(amountPaise) {
  if (amountPaise == null) return '—';
  return '₹' + (Number(amountPaise) / 100).toFixed(2);
}

function RecoveryStateBadge({ state }) {
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded border text-2xs font-mono',
      RECOVERY_STATE_STYLE[state] ?? 'text-txt-muted border-bg-border',
    )}>
      {RECOVERY_STATE_LABEL[state] ?? String(state ?? '—').toUpperCase()}
    </span>
  );
}

function OutcomeNote({ outcome }) {
  if (!outcome) return null;
  const copy = outcome.failed ? null : OUTCOME_COPY[outcome.value];
  const Icon = copy?.icon ?? AlertTriangle;
  const tone = copy?.tone ?? 'bear';
  return (
    <div className={cn(
      'mt-2 flex items-start gap-1.5 text-2xs rounded border px-2 py-1.5',
      tone === 'bull' && 'text-bull bg-bull/5 border-bull/30',
      tone === 'warn' && 'text-warn bg-warn/5 border-warn/30',
      tone === 'bear' && 'text-bear bg-bear/5 border-bear/30',
    )}>
      <Icon size={12} className="shrink-0 mt-0.5" />
      <span>{copy ? copy.text : CHECK_FAILED_TEXT}</span>
    </div>
  );
}

/**
 * Owner-scoped pending-payment recovery.
 *
 * Reads the authenticated account's own unresolved payment orders through the
 * backend's paginated `GET /billing/orders/pending` and lets the owner ask the
 * backend to re-check authoritative provider evidence. Checking status never
 * creates a Razorpay order, never charges the customer, and never releases a
 * license reservation — the backend decides the outcome.
 *
 * `itemType` keeps the subscription page showing subscription payments and the
 * recharge page showing license-pack payments. The backend list is owner-scoped
 * and paginated but is not filtered by item type, so rows of the other type are
 * hidden here rather than requested separately.
 */
export default function PendingPaymentsPanel({
  itemType,
  title = 'Pending payments',
  description = 'A completed payment can take a short while to confirm. Checking status here never creates a new payment and never charges you again.',
  enabled = true,
}) {
  const [page, setPage] = useState(0);
  // Per-order outcome of the last status check, held only for this mounted view.
  const [outcomes, setOutcomes] = useState({});
  // Exactly one in-flight check at a time, so a double click or a second row
  // cannot submit a duplicate recovery request.
  const [checkingOrderId, setCheckingOrderId] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useMyPendingPayments({
    limit:  PAGE_SIZE,
    offset: page * PAGE_SIZE,
    enabled,
  });
  const { mutateAsync: verifyPendingPayment } = useVerifyPendingPayment();

  const allItems = Array.isArray(data?.items) ? data.items : [];
  const items = itemType ? allItems.filter((item) => item.item_type === itemType) : allItems;
  const total = Number(data?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function handleCheck(orderId) {
    if (checkingOrderId != null) return;
    setCheckingOrderId(orderId);
    setOutcomes((prev) => ({ ...prev, [orderId]: null }));
    try {
      const result = await verifyPendingPayment(orderId);
      setOutcomes((prev) => ({ ...prev, [orderId]: { value: result?.outcome ?? 'pending' } }));
    } catch {
      setOutcomes((prev) => ({ ...prev, [orderId]: { failed: true } }));
    } finally {
      setCheckingOrderId(null);
    }
  }

  if (!enabled) return null;
  if (!isLoading && !error && total === 0) return null;

  return (
    <div className="border border-bg-border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-txt-primary">
            <Clock size={15} />
            {title}
          </div>
          <p className="text-2xs text-txt-muted mt-1">{description}</p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="btn-ghost text-2xs flex items-center gap-1.5 py-1 disabled:opacity-50"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-xs text-bear">
          Pending payments could not be loaded right now.
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-txt-muted">No pending payments on this page.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => {
            const isChecking = checkingOrderId === item.order_id;
            return (
              <li
                key={item.order_id}
                className="border border-bg-border rounded bg-bg-surface/30 px-3 py-2.5"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-1">
                    <div className="text-xs font-medium text-txt-primary truncate">
                      {item.item_name ?? (item.item_type === 'plan' ? 'Subscription plan' : 'License pack')}
                    </div>
                    <div className="text-2xs text-txt-muted font-mono">
                      {fmtAmount(item.amount_paise)} {item.currency ?? ''} · {fmtDateTime(item.created_at)}
                    </div>
                    {item.razorpay_order_id && (
                      <div className="text-2xs text-txt-muted font-mono truncate">
                        Order: {item.razorpay_order_id}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <RecoveryStateBadge state={item.recovery_state} />
                    <button
                      onClick={() => handleCheck(item.order_id)}
                      disabled={checkingOrderId != null}
                      className="btn-ghost text-2xs py-1 px-2 border border-bg-border disabled:opacity-50"
                    >
                      {isChecking ? 'Checking…' : 'Check payment status'}
                    </button>
                  </div>
                </div>
                <OutcomeNote outcome={outcomes[item.order_id]} />
              </li>
            );
          })}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-2xs text-txt-muted">
          <span>Page {page + 1} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-ghost text-2xs py-1 px-2 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-ghost text-2xs py-1 px-2 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
